// City catalogue for programmatic SEO landing pages.
// Each city has ONE canonical language (the dominant language of the city/country)
// and ONE URL. No language switching, no duplicate routes per city — keeps the
// SEO simple and avoids thin/duplicate content.
//
//   Germany (DE):     /qr-menue-berlin, /qr-menue-muenchen, /qr-menue-hamburg, /qr-menue-koeln
//   Austria (DE):     /qr-menue-wien
//   Switzerland (DE): /qr-menue-zuerich
//   UK / IE (EN):     /qr-menu-london, /qr-menu-manchester, /qr-menu-dublin
//   Turkey (TR):      /qr-menu-istanbul, /qr-menu-ankara, /qr-menu-izmir

const SETUP_FAQ = (cityName, lang) => ({
  q: {
    de: `Wie schnell kann ein Restaurant in ${cityName} ein QR-Menü einrichten?`,
    en: `How quickly can a restaurant in ${cityName} set up a QR menu?`,
    tr: `${cityName}'de bir restoran QR menüyü ne kadar sürede kurabilir?`,
  }[lang],
  a: {
    de: "In der Regel unter 10 Minuten. Du erstellst ein Konto, lädst dein Menü hoch, generierst deinen QR-Code und druckst ihn — alles ohne Programmierkenntnisse.",
    en: "Typically in under 10 minutes. Create your account, upload your menu, generate your QR code and print it — no coding required.",
    tr: "Genellikle 10 dakikadan az. Hesap oluşturursunuz, menünüzü yüklersiniz, QR kodunu üretip yazdırırsınız — kod gerekmez.",
  }[lang],
});

const MULTILANG_FAQ = (cityName, lang) => ({
  q: {
    de: `Unterstützt das Menü mehrere Sprachen für Touristen in ${cityName}?`,
    en: `Does the menu support multiple languages for tourists in ${cityName}?`,
    tr: `Menü, ${cityName}'deki turistler için çoklu dil destekliyor mu?`,
  }[lang],
  a: {
    de: "Ja. qrmenu-pro.com unterstützt Deutsch, Englisch und Türkisch. Deine Gäste sehen dein Menü automatisch in ihrer Sprache.",
    en: "Yes. qrmenu-pro.com supports German, English and Turkish. Your guests automatically see the menu in their language.",
    tr: "Evet. qrmenu-pro.com Almanca, İngilizce ve Türkçe destekler. Misafirleriniz menünüzü otomatik olarak kendi dillerinde görür.",
  }[lang],
});

const PRICING_FAQ = (cityName, lang) => ({
  q: {
    de: `Was kostet ein QR-Menü für ein Restaurant in ${cityName}?`,
    en: `How much does a QR menu cost for a restaurant in ${cityName}?`,
    tr: `${cityName}'de bir restoran için QR menü ne kadar?`,
  }[lang],
  a: {
    de: "Der Starter-Plan kostet 20 €/Monat, der Pro-Plan 40 €/Monat. Du startest mit einer 15-tägigen kostenlosen Testphase — ohne Kreditkarte.",
    en: "Starter is €20/month, Pro is €40/month. You start with a 15-day free trial — no credit card required.",
    tr: "Starter ayda 20 €, Pro ayda 40 €. 15 günlük ücretsiz deneme ile başlarsınız — kredi kartı gerekmez.",
  }[lang],
});

// Each city: lang, country, slug (without leading "/"), display name, intro,
// neighborhoods, localKeyword, and exactly one city-specific extraFaq.
// The component prepends 3 common FAQs (extraFaq + setup + multilang + pricing = 4 total).
const RAW_CITIES = [
  // ---------- Germany / Austria / Switzerland (German) ----------
  {
    id: "berlin",
    lang: "de",
    country: "DE",
    slug: "qr-menue-berlin",
    name: "Berlin",
    venues: "11.000+",
    intro:
      "Berlin ist das Herz der deutschen Gastronomie mit über 11.000 Restaurants, Cafés und Bars — von Currywurst-Buden in Mitte bis Sterne-Restaurants in Charlottenburg.",
    neighborhoods: ["Mitte", "Kreuzberg", "Prenzlauer Berg", "Friedrichshain", "Charlottenburg"],
    localKeyword: "QR Speisekarte Berlin",
    extraFaq: {
      q: "Welche Sprachen empfehlen sich für ein Restaurant in Berlin?",
      a: "Berlin ist eine der internationalsten Städte Europas. Wir empfehlen mindestens Deutsch und Englisch, zusätzlich Türkisch wegen der großen türkischen Community.",
    },
  },
  {
    id: "muenchen",
    lang: "de",
    country: "DE",
    slug: "qr-menue-muenchen",
    name: "München",
    venues: "5.500+",
    intro:
      "München vereint bayerische Wirtshäuser, italienische Trattorien und moderne Cafés in der Altstadt, Schwabing und Glockenbach — perfekt für ein klares digitales Menü.",
    neighborhoods: ["Altstadt", "Schwabing", "Glockenbachviertel", "Maxvorstadt", "Haidhausen"],
    localKeyword: "QR Speisekarte München",
    extraFaq: {
      q: "Funktioniert das QR-Menü auch während des Oktoberfests?",
      a: "Ja. Unser System skaliert für hohe Last und mehrere hundert gleichzeitige Scans pro Minute — perfekt für Bierzelt und Festwiesen-Restaurants.",
    },
  },
  {
    id: "hamburg",
    lang: "de",
    country: "DE",
    slug: "qr-menue-hamburg",
    name: "Hamburg",
    venues: "4.800+",
    intro:
      "Von Fischbrötchen am Hafen bis Fine-Dining an der Außenalster — Hamburgs Gastronomie ist vielfältig, international und perfekt für digitale Menüs.",
    neighborhoods: ["St. Pauli", "Sternschanze", "Eppendorf", "HafenCity", "Altona"],
    localKeyword: "QR Speisekarte Hamburg",
    extraFaq: {
      q: "Eignet sich das QR-Menü auch für Restaurants am Hafen mit Tourist:innen?",
      a: "Ja. Touristen scannen den Code, sehen das Menü sofort in ihrer Sprache und bestellen ohne Wartezeit. Ideal für Reeperbahn, Landungsbrücken und HafenCity.",
    },
  },
  {
    id: "koeln",
    lang: "de",
    country: "DE",
    slug: "qr-menue-koeln",
    name: "Köln",
    venues: "3.900+",
    intro:
      "Köln lebt von Brauhäusern, Karneval und Veedeln — von der Altstadt bis Belgisches Viertel und Ehrenfeld bietet ein digitales QR-Menü Klarheit für jeden Gast.",
    neighborhoods: ["Altstadt", "Belgisches Viertel", "Ehrenfeld", "Südstadt", "Nippes"],
    localKeyword: "QR Speisekarte Köln",
    extraFaq: {
      q: "Können wir Karnevals-Specials kurzfristig ins Menü aufnehmen?",
      a: "Ja — Änderungen erscheinen sofort im Live-Menü. Perfekt für saisonale Karnevals-Drinks und Aktionen am Rosenmontag.",
    },
  },
  {
    id: "wien",
    lang: "de",
    country: "AT",
    slug: "qr-menue-wien",
    name: "Wien",
    venues: "6.400+",
    intro:
      "Wien lebt von Kaffeehäusern, Heurigen und Beislkultur — von Stephansplatz bis Naschmarkt ist ein klares, mehrsprachiges QR-Menü Gold wert.",
    neighborhoods: ["Innere Stadt", "Neubau", "Mariahilf", "Leopoldstadt", "Wieden"],
    localKeyword: "QR Speisekarte Wien",
    extraFaq: {
      q: "Funktioniert das QR-Menü auch im traditionellen Wiener Kaffeehaus?",
      a: "Ja. Unsere Premium-Themes (Elegant, Klassisch) passen perfekt zum Charme der Wiener Kaffeehauskultur — diskret, edel, mehrsprachig.",
    },
  },
  {
    id: "zuerich",
    lang: "de",
    country: "CH",
    slug: "qr-menue-zuerich",
    name: "Zürich",
    venues: "2.100+",
    intro:
      "Zürich verbindet Schweizer Präzision mit internationaler Gastronomie — Restaurants am Zürichsee, im Niederdorf und Kreis 4 nutzen QR-Menüs für effizientes Service.",
    neighborhoods: ["Niederdorf", "Kreis 4", "Kreis 5", "Seefeld", "Enge"],
    localKeyword: "QR Speisekarte Zürich",
    extraFaq: {
      q: "Können wir Preise in CHF statt in EUR anzeigen?",
      a: "Ja. Du wählst die Währung pro Restaurant frei — CHF, EUR, GBP, TRY und mehr werden unterstützt.",
    },
  },

  // ---------- United Kingdom / Ireland (English) ----------
  {
    id: "london",
    lang: "en",
    country: "GB",
    slug: "qr-menu-london",
    name: "London",
    venues: "18,000+",
    intro:
      "London is Europe's biggest food city with 18,000+ restaurants — from Shoreditch pubs to Mayfair fine dining, multilingual QR menus shine.",
    neighborhoods: ["Soho", "Shoreditch", "Covent Garden", "Mayfair", "Camden"],
    localKeyword: "Digital QR menu London",
    extraFaq: {
      q: "Can London guests order easily in their own language?",
      a: "Yes. London is one of the world's most multilingual cities — our system automatically switches between English, German and Turkish based on device language.",
    },
  },
  {
    id: "manchester",
    lang: "en",
    country: "GB",
    slug: "qr-menu-manchester",
    name: "Manchester",
    venues: "3,400+",
    intro:
      "Manchester is the UK North's food hub — from the Curry Mile to the Northern Quarter, QR menus are perfect for students and international guests.",
    neighborhoods: ["Northern Quarter", "Deansgate", "Spinningfields", "Ancoats", "Rusholme"],
    localKeyword: "Digital QR menu Manchester",
    extraFaq: {
      q: "Do QR menus work for busy student cafés?",
      a: "Yes. With Live Table Service, students call waiter or bill straight from the menu — perfect for busy cafés in Rusholme or Fallowfield.",
    },
  },
  {
    id: "dublin",
    lang: "en",
    country: "IE",
    slug: "qr-menu-dublin",
    name: "Dublin",
    venues: "2,300+",
    intro:
      "Dublin blends traditional pubs with a modern craft-beer scene — Temple Bar, the Docklands and Drumcondra benefit from fast, multilingual QR menus.",
    neighborhoods: ["Temple Bar", "Docklands", "Drumcondra", "Ballsbridge", "Stoneybatter"],
    localKeyword: "Digital QR menu Dublin",
    extraFaq: {
      q: "Can traditional pubs map their classic menu digitally?",
      a: "Yes. You keep the familiar structure — Beers, Whiskey, Mains, Sides — and enrich it with allergens, prices and photos on a clean mobile page.",
    },
  },

  // ---------- Turkey (Turkish) ----------
  {
    id: "istanbul",
    lang: "tr",
    country: "TR",
    slug: "qr-menu-istanbul",
    name: "İstanbul",
    venues: "40.000+",
    intro:
      "İstanbul; 40.000'i aşkın restoran, kafe ve meyhanesiyle Türkiye'nin gastronomi merkezidir — Kadıköy'den Beyoğlu'na, Bebek'ten Karaköy'e dijital QR menü hızlı servis sağlar.",
    neighborhoods: ["Beyoğlu", "Kadıköy", "Karaköy", "Bebek", "Beşiktaş"],
    localKeyword: "QR Menü İstanbul",
    extraFaq: {
      q: "Boğaz'daki turistik restoranlar için çok dilli QR menü uygun mu?",
      a: "Evet. Sistemimiz Türkçe, İngilizce ve Almanca'yı otomatik tanır — yabancı misafirler menüyü kendi dillerinde görür, garsonla iletişim sorunu yaşamaz.",
    },
  },
  {
    id: "ankara",
    lang: "tr",
    country: "TR",
    slug: "qr-menu-ankara",
    name: "Ankara",
    venues: "8.500+",
    intro:
      "Ankara; başkent kimliğiyle iş yemekleri, kebap ustaları ve modern brunch mekânlarını buluşturur — Çankaya'dan Kızılay'a QR menü iş hayatının hızına uyum sağlar.",
    neighborhoods: ["Çankaya", "Kızılay", "Tunalı Hilmi", "Bahçelievler", "Çayyolu"],
    localKeyword: "QR Menü Ankara",
    extraFaq: {
      q: "İş yemekleri için fatura ve allerjen bilgisi gösterilebilir mi?",
      a: "Evet. Her ürün için allerjen ikonları, besin değerleri ve fatura uyumlu fiyatlandırma ekleyebilirsiniz — kurumsal müşterileriniz için ideal.",
    },
  },
  {
    id: "izmir",
    lang: "tr",
    country: "TR",
    slug: "qr-menu-izmir",
    name: "İzmir",
    venues: "6.200+",
    intro:
      "İzmir; Ege mutfağı, balık lokantaları ve modern kafelerin başkentidir — Alsancak'tan Karşıyaka'ya, Foça'dan Çeşme'ye QR menü yaz turizmiyle birlikte hızla yayılıyor.",
    neighborhoods: ["Alsancak", "Karşıyaka", "Bornova", "Konak", "Bostanlı"],
    localKeyword: "QR Menü İzmir",
    extraFaq: {
      q: "Yaz sezonunda kıyıdaki açık hava restoranları için uygun mu?",
      a: "Evet. QR menüler kâğıt menülere göre rüzgâra, ıslanmaya ve yıpranmaya dayanıklıdır — kıyı restoranları için ideal. Bir kez yazdırın, tüm sezon kullanın.",
    },
  },
];

// Augment each city with the localized FAQ block (4 entries) and a unified
// cityPath helper. All consumers should use cityPath(city) and read city.lang
// for content rendering — no more per-language slug juggling.
export const CITIES = RAW_CITIES.map((c) => ({
  ...c,
  path: `/${c.slug}`,
  faq: [
    c.extraFaq,
    SETUP_FAQ(c.name, c.lang),
    MULTILANG_FAQ(c.name, c.lang),
    PRICING_FAQ(c.name, c.lang),
  ],
}));

const SLUG_INDEX = {};
CITIES.forEach((c) => { SLUG_INDEX[c.slug] = c; });

export function findCityBySlug(slug) {
  return SLUG_INDEX[slug] || null;
}

export function cityPath(city) {
  return city.path;
}
